import {
  AddUniversalShoppingBasketPromoCodeRequest,
  RemoveUniversalShoppingBasketPromoCodeRequest,
} from '#src/generated/graphql';
import { ErrorBoundary } from '@edreamsodigeo/frontend-core';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

import {
  useAddPromoCodeToShoppingBasket,
  useRemovePromoCodeFromShoppingBasket,
} from './ShoppingBasketPromoCodeProvider.hooks';

type ShoppingBasketPromoCodeProviderProps = {
  shoppingBasketId: string;
  children: React.ReactNode;
};

type ShoppingBasketPromoCodeContextType = {
  addPromoCodeToShoppingBasket: (
    args: Omit<AddUniversalShoppingBasketPromoCodeRequest, 'shoppingBasketId'>
  ) => Promise<unknown>;
  removePromoCodeFromShoppingBasket: (
    args: Omit<
      RemoveUniversalShoppingBasketPromoCodeRequest,
      'shoppingBasketId'
    >
  ) => Promise<unknown>;
};

const ERROR_BOUNDARY_NAME = 'ShoppingBasketPromoCodeProvider';

const DEFAULT_RESPONSE = {
  success: false,
};

const ShoppingBasketPromoCodeContext =
  createContext<ShoppingBasketPromoCodeContextType>({
    addPromoCodeToShoppingBasket: () => Promise.resolve(DEFAULT_RESPONSE),
    removePromoCodeFromShoppingBasket: () => Promise.resolve(DEFAULT_RESPONSE),
  });

export const useShoppingBasketPromoCodes = () =>
  useContext(ShoppingBasketPromoCodeContext);

export const ShoppingBasketPromoCodeProvider = ({
  children,
  shoppingBasketId,
}: ShoppingBasketPromoCodeProviderProps) => {
  const { addPromoCodeToShoppingBasket } = useAddPromoCodeToShoppingBasket();

  const { removePromoCodeFromShoppingBasket } =
    useRemovePromoCodeFromShoppingBasket();

  const add = useCallback(
    ({ code }) => {
      return addPromoCodeToShoppingBasket({
        shoppingBasketId,
        code,
      });
    },
    [addPromoCodeToShoppingBasket, shoppingBasketId]
  );

  const remove = useCallback(
    ({ id }) => {
      return removePromoCodeFromShoppingBasket({
        shoppingBasketId,
        id,
      });
    },
    [removePromoCodeFromShoppingBasket, shoppingBasketId]
  );

  const value = useMemo(
    () => ({
      addPromoCodeToShoppingBasket: add,
      removePromoCodeFromShoppingBasket: remove,
    }),
    [add, remove]
  );

  return (
    <ErrorBoundary name={ERROR_BOUNDARY_NAME}>
      <ShoppingBasketPromoCodeContext.Provider value={value}>
        {children}
      </ShoppingBasketPromoCodeContext.Provider>
    </ErrorBoundary>
  );
};
